<template>
  <div>
    <div class="flex mb-base mr-base lg:mr-0">
      <VSearch
        class="flex-grow mr-base"
        @onSearch="handleSearch"
        @onResetSearch="() => handleSearch({ value: '' })"
        :updateUrl="false"
        :placeholder="$t('screens.templateSelectorScreen.searchTemplates')"
        :ec="`${ec}_template_selector`"
        data-et="search"
      />
       <ListOrCardViewSwitcher
        :defaultShowCard="showCardList"
        @setShowCardView="setListTypeView"
      />
    </div>

    <template v-if="!templatesDisplay.length && !pendingTemplates">
      <NoTemplatesEmptyState
        v-if="!isSearch"
        v-on="$listeners"
      />
      <TemplateSearchEmptyState v-else />
    </template>

    <template v-else>
      <div
        ref="listContainer"
        :class="listClass"
      >
        <LoadingSpinner
          v-if="
            placeholderTemplates ||
            (fetchingTemplates && !fetchingNextTemplates && !pendingTemplates)
          "
          :delay="true"
        />
        <div v-show="showCardList">
          <SelectableCardList
            :list="templatesDisplay"
            :size="totalTemplates"
            :ec="ec"
            :key="`card-${showCardList}`"
            itemType="template"
            @toggled="toggleSelected"
            :loading="pendingTemplates"
            :disabled="fetchingTemplates"
            aria-label="Templates"
          />
          <SelectableCardList
            v-if="hasMore && fetchingNextTemplates"
            :list="[]"
            :loading="true"
            :aria-label="$t('screens.templateSelectorScreen.loadingTemplates')"
            role="status"
          />
        </div>
        <SelectableList
          v-show="!showCardList"
          :list="templatesDisplay"
          :loading="pendingTemplates"
          :size="totalTemplates"
          :ec="ec"
          :key="`list-${showCardList}`"
          @toggled="toggleSelected"
          itemType="template"
          aria-label="Templates"
        />
        <div
          v-if="hasMore && !fetchingNextTemplates"
          class="flex justify-center w-full -bottom-xl"
        >
          <BaseButton
            variant="invisible"
            @click.native="$emit('next')"
          >
            {{ $t('screens.templateSelectorScreen.moreTemplates') }}
          </BaseButton>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
// @ts-check
import { computed, defineProps, ref } from 'vue';
import { useInfiniteScroll, useLocalStorage } from '@vueuse/core';

import localeDateFormatter from '@/utils/locale-date-formatter';
import i18n from '@/locales';

import VSearch from '@/components/deprecated/organisms/Search.vue';
import BaseButton from '@/components/library/buttons/BaseButton.vue';
import LoadingSpinner from '@/components/tailwind/LoadingSpinner.vue';
import NoTemplatesEmptyState from '@/components/ui/envelopeCreate/NoTemplatesEmptyState.vue';
import TemplateSearchEmptyState from '@/components/ui/envelopeCreate/TemplateSearchEmptyState.vue';
import { ListOrCardViewSwitcher } from '@/components/library/viewSwitchers/ListOrCardViewSwitcher';
import { SelectableCardList } from '@/components/library/lists/viewSwitcherList/SelectableCardList';
import { SelectableList } from '@/components/library/lists/viewSwitcherList/SelectableList';

/** @typedef {{fingerprint: string, title: string}} TTemplate */
/** @typedef {import('@/store/modules/bulkSend/models/Templates').TListTemplate} TListTemplate */

const props = defineProps({
  /** @type {import('vue').PropOptions<TListTemplate[]>} */
  templates: {
    required: true,
  },
  totalTemplates: {
    type: Number,
    required: true,
  },
  pendingTemplates: {
    type: Boolean,
    required: true,
  },
  fetchingTemplates: {
    type: Boolean,
    required: true,
  },
  fetchingNextTemplates: {
    type: Boolean,
    required: true,
  },
  placeholderTemplates: {
    type: Boolean,
    required: true,
  },
  hasMore: {
    type: Boolean,
    required: true,
  },
  /** @type {import('vue').PropOptions<TTemplate[]>} */
  selectedTemplates: {
    type: Array,
    required: true,
  },
  listClass: String,
  ec: String,
});
const emit = defineEmits(['add', 'remove', 'search', 'next']);

const isSearch = ref(false);

const showCardList = useLocalStorage('templateSelectorDefaultCardViewPreference', true);

function setListTypeView(setting) {
  showCardList.value = setting;
}

/** @type {import('vue').Ref<HTMLDivElement|undefined>} */
const listContainer = ref();

function handleScroll() {
  if (!props.fetchingNextTemplates) {
    emit('next');
  }
}

useInfiniteScroll(listContainer, handleScroll, {
  distance: 10,
  canLoadMore: () => props.hasMore && !props.fetchingNextTemplates,
});

function handleSearch({ value }) {
  listContainer.value?.scrollTo(0, 0);
  isSearch.value = !!value;
  emit('search', value);
}

/**
 * Toggles the selected state of a template.
 *
 * @param {string} fingerprint - The fingerprint of the template.
 * @param {string} title - The title of the template.
 */
function toggleSelected(fingerprint, title) {
  if (
    props.selectedTemplates?.some(item => item.fingerprint === fingerprint)
  ) {
    emit('remove', fingerprint);
  } else {
    emit('add', fingerprint, title);
  }
}

/**
 * Formats a timestamp into a human-readable date format.
 *
 * @param {number} timestamp - The timestamp to format.
 * @returns {string} The formatted date string.
 */
function formatTemplateDate(timestamp) {
  const date = localeDateFormatter('concise', timestamp);
  return i18n.t('screens.templateSelectorScreen.createdAt', { date });
}

const templatesDisplay = computed(() => (props.templates || []).map(template => ({
    title: template.title,
    id: template.fingerprint,
    secondaryInfo: formatTemplateDate(template.created_at || 0),
    thumbnail: template.images[0],
    selected: props.selectedTemplates?.some(item => template.fingerprint === item.fingerprint),
  })));
</script>
